import styled from '@emotion/styled'
import React from 'react'
import { useDebounce, useUnmount } from 'react-use'

import { noop } from '@livechat/data-utils'

import Button from 'components/Button'
import Input from 'components/fields/Input'
import { WhereAddressIcon } from 'components/icons'
import { searchPlaces } from 'lib/api'

const REQUEST_DEBOUNCE_TIME = 200

const Container = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space3};
`

const SearchResults = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: ${({ theme }) => theme.spaces.space2};
	padding: ${({ theme }) => theme.spaces.space5};
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	background-color: ${({ theme }) => theme.colors.light.secondaryBackgroundColor};
	box-shadow: ${({ theme }) => theme.boxShadows.xs};
	margin: ${({ theme }) => theme.spaces.space1};
	max-width: 380px;
`

const SearchResultButton = styled(Button)`
	display: flex;
	padding: ${({ theme }) => theme.spaces.space3};
	justify-content: flex-start;
	border-radius: ${({ theme }) => theme.borderRadiuses.md};

	:hover {
		background-color: ${({ theme }) => theme.colors.light.surfaceVariant};
	}
`

const SearchResultsDescription = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	${({ theme }) => theme.typography.caption};
`

const NoResults = styled.div`
	width: 100%;
	display: flex;
	padding: ${({ theme }) => theme.spaces.space4};
	gap: ${({ theme }) => theme.spaces.space3};
	justify-content: flex-start;
	align-items: center;

	i {
		font-style: normal;
		${({ theme }) => theme.typography.caption};
	}
`

type Props = {
	onChoosePlace: (placeId: string | null) => void
}

type State = {
	query: string
	resultsNotFound: boolean
	results: Array<{ description: string; placeId: string }>
}

type Action =
	| { type: 'set/query'; payload: State['query'] }
	| { type: 'set/results'; payload: State['results'] }
	| { type: 'reset' }

const initialState: State = {
	query: '',
	resultsNotFound: false,
	results: [],
}

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case 'set/query': {
			return action.payload
				? {
						...state,
						query: action.payload,
						resultsNotFound: false,
				  }
				: initialState
		}

		case 'set/results':
			return {
				...state,
				results: action.payload,
				resultsNotFound: action.payload.length === 0,
			}

		case 'reset':
			return initialState

		default:
			return state
	}
}

function PlaceSearch({ onChoosePlace }: Props) {
	const [{ query, results, resultsNotFound: noResults }, dispatch] = React.useReducer(reducer, initialState)
	const searchRequestAbortController = React.useRef<AbortController | null>(null)

	function handleSelectPlace(placeId: string) {
		dispatch({ type: 'reset' })
		onChoosePlace(placeId)
	}

	const [, cancelDebounce] = useDebounce(
		() => {
			if (!query) {
				return
			}

			if (searchRequestAbortController.current) {
				searchRequestAbortController.current.abort()
			}

			searchRequestAbortController.current = new AbortController()

			searchPlaces(query, searchRequestAbortController.current.signal)
				.then((results) => dispatch({ type: 'set/results', payload: results }))
				.catch(noop)
				.finally(() => {
					searchRequestAbortController.current = null
				})
		},
		REQUEST_DEBOUNCE_TIME,
		[query],
	)

	useUnmount(() => {
		if (searchRequestAbortController.current) {
			searchRequestAbortController.current.abort()
		}

		cancelDebounce()
	})

	return (
		<Container data-testid="place-search">
			<>
				<Input
					value={query}
					label="Search your business"
					placeholder="Enter full name or website address"
					onChange={(event) => {
						dispatch({ type: 'set/query', payload: event.target.value })
					}}
				/>
				{(noResults || results.length > 0) && (
					<SearchResults>
						{noResults && (
							<NoResults>
								<WhereAddressIcon />
								<i>
									Business not found. Only profiles with a physical address can be added. Try providing a link instead.
								</i>
							</NoResults>
						)}
						{results.map((result) => (
							<SearchResultButton
								fullWidth
								kind="text"
								disableHover
								key={result.placeId}
								onClick={() => handleSelectPlace(result.placeId)}
							>
								<SearchResultsDescription>{result.description}</SearchResultsDescription>
							</SearchResultButton>
						))}
					</SearchResults>
				)}
			</>
		</Container>
	)
}

export default PlaceSearch
